import React from "react";

/**
 * UserInitials — deterministic initials-only avatar.
 *
 * Replaces the previous `<img src={user.avatar_url}>` treatment across the app.
 * The bg colour is derived from the user's name so the same person always
 * gets the same tile, but everyone's tile stays visually distinct. Uses only
 * Tailwind classes — no runtime CSS variables — so it renders correctly in
 * both light and dark themes without extra work.
 *
 * Ten muted brand-adjacent hues, all with WCAG-AA text-on-bg contrast.
 */
const PALETTE = [
  "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-100",
  "bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-100",
  "bg-sky-100 text-sky-800 dark:bg-sky-900/60 dark:text-sky-100",
  "bg-rose-100 text-rose-800 dark:bg-rose-900/60 dark:text-rose-100",
  "bg-violet-100 text-violet-800 dark:bg-violet-900/60 dark:text-violet-100",
  "bg-teal-100 text-teal-800 dark:bg-teal-900/60 dark:text-teal-100",
  "bg-orange-100 text-orange-800 dark:bg-orange-900/60 dark:text-orange-100",
  "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-100",
  "bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100",
  "bg-lime-100 text-lime-800 dark:bg-lime-900/60 dark:text-lime-100",
];

function initialsFromName(name, email) {
  const source = (name || "").trim() || (email || "").split("@")[0] || "";
  if (!source) return "?";
  // Prefer 2 letters — first char of the first two whitespace-separated tokens,
  // fall back to the first two chars of a single-token name / email prefix.
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

function colourFor(name, email) {
  const key = (name || email || "").toLowerCase();
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return PALETTE[hash % PALETTE.length];
}

/**
 * Props:
 *   - name:      full name (preferred)
 *   - email:     fallback source when name is missing
 *   - size:      Tailwind height/width classes, e.g. "h-8 w-8" (default)
 *   - className: extra classes appended to the outer div
 *   - testId:    optional data-testid for automation
 */
export default function UserInitials({ name, email, size = "h-8 w-8", className = "", testId }) {
  const initials = initialsFromName(name, email);
  const colour = colourFor(name, email);
  return (
    <div
      className={`${size} ${colour} rounded-full grid place-items-center font-bold tracking-tight select-none shrink-0 ${className}`}
      data-testid={testId}
      title={name || email || undefined}
      aria-label={name || email || "user"}
    >
      <span className="text-[11px] leading-none">{initials}</span>
    </div>
  );
}
